import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Typography, Paper, Button, Chip } from '@mui/material';
import { RideSchema } from '../../types/simulation';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Fix for default markers in Leaflet with React
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

interface RideMapProps {
  rides: RideSchema[];
}

export const RideMap: React.FC<RideMapProps> = ({ rides }) => {
  const { t } = useTranslation('simulation');
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<L.Map | null>(null);
  const markersRef = useRef<{pickup: L.Marker, dropoff: L.Marker, line: L.Polyline, rideIndex: number}[]>([]);
  const [selectedRideIndex, setSelectedRideIndex] = useState<number | null>(null);

  // Effect for initializing map and creating markers only when rides data changes
  useEffect(() => {
    if (!mapRef.current || rides.length === 0) return;

    // Initialize map only once
    if (!mapInstanceRef.current) {
      // Calculate center from ride data
      const avgLat = rides.reduce((sum, ride) => sum + ride.from_latitude, 0) / rides.length;
      const avgLng = rides.reduce((sum, ride) => sum + ride.from_longitude, 0) / rides.length;

      mapInstanceRef.current = L.map(mapRef.current).setView([avgLat, avgLng], 12);
      
      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '© OpenStreetMap contributors'
      }).addTo(mapInstanceRef.current);
    }

    // Clear existing markers and lines
    markersRef.current.forEach(({pickup, dropoff, line}) => {
      mapInstanceRef.current?.removeLayer(pickup);
      mapInstanceRef.current?.removeLayer(dropoff);
      mapInstanceRef.current?.removeLayer(line);
    });
    markersRef.current = [];

    // Add markers and lines for rides (with default styling)
    rides.forEach((ride, index) => {
      // Pickup marker (default style)
      const pickupMarker = L.marker([ride.from_latitude, ride.from_longitude], {
        icon: L.divIcon({
          className: 'custom-pickup-marker',
          html: `<div style="
            background-color: #4caf50;
            width: 12px;
            height: 12px;
            border-radius: 50%;
            border: 2px solid white;
            box-shadow: 0 2px 4px rgba(0,0,0,0.3);
            cursor: pointer;
          "></div>`,
          iconSize: [12, 12],
          iconAnchor: [6, 6]
        })
      }).addTo(mapInstanceRef.current!);
      
      // Add click event to pickup marker
      pickupMarker.on('click', () => {
        setSelectedRideIndex(selectedRideIndex === index ? null : index);
      });

      // Dropoff marker (default style)
      const dropoffMarker = L.marker([ride.to_latitude, ride.to_longitude], {
        icon: L.divIcon({
          className: 'custom-dropoff-marker',
          html: `<div style="
            background-color: #f44336;
            width: 12px;
            height: 12px;
            border-radius: 50%;
            border: 2px solid white;
            box-shadow: 0 2px 4px rgba(0,0,0,0.3);
            cursor: pointer;
          "></div>`,
          iconSize: [12, 12],
          iconAnchor: [6, 6]
        })
      }).addTo(mapInstanceRef.current!);
      
      // Add click event to dropoff marker
      dropoffMarker.on('click', () => {
        setSelectedRideIndex(selectedRideIndex === index ? null : index);
      });

      // Route line (default style)
      
      const routeLine = L.polyline([
        [ride.from_latitude, ride.from_longitude],
        [ride.to_latitude, ride.to_longitude]
      ], {
        color: '#2196f3',
        weight: 2,
        opacity: 0.6
      }).addTo(mapInstanceRef.current!);
      
      // Add click event to route line
      routeLine.on('click', () => {
        setSelectedRideIndex(selectedRideIndex === index ? null : index);
      });

      markersRef.current.push({
        pickup: pickupMarker,
        dropoff: dropoffMarker,
        line: routeLine,
        rideIndex: index
      });
    });

    return () => {
      // Cleanup on unmount
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
      }
    };
  }, [rides]);

  // Effect for fitting bounds only when rides data changes
  useEffect(() => {
    if (rides.length > 0 && mapInstanceRef.current && markersRef.current.length > 0) {
      const allLayers = markersRef.current.flatMap(({pickup, dropoff, line}) => [pickup, dropoff, line]);
      const group = L.featureGroup(allLayers);
      mapInstanceRef.current.fitBounds(group.getBounds().pad(0.1));
    }
  }, [rides]);

  // Effect for updating marker styles when selection changes
  useEffect(() => {
    if (!mapInstanceRef.current || markersRef.current.length === 0) return;

    markersRef.current.forEach(({pickup, dropoff, line, rideIndex}) => {
      const isSelected = selectedRideIndex === rideIndex;
      const hasSelection = selectedRideIndex !== null;
      
      // Determine colors and sizes based on selection
      const pickupColor = isSelected ? '#4caf50' : (hasSelection && !isSelected) ? '#9e9e9e' : '#4caf50';
      const dropoffColor = isSelected ? '#f44336' : (hasSelection && !isSelected) ? '#9e9e9e' : '#f44336';
      const markerSize = isSelected ? 16 : (hasSelection && !isSelected) ? 10 : 12;
      const borderWidth = isSelected ? 3 : 2;
      
      // Update pickup marker style
      pickup.setIcon(L.divIcon({
        className: 'custom-pickup-marker',
        html: `<div style="
          background-color: ${pickupColor};
          width: ${markerSize}px;
          height: ${markerSize}px;
          border-radius: 50%;
          border: ${borderWidth}px solid white;
          box-shadow: 0 2px 4px rgba(0,0,0,0.3);
          cursor: pointer;
        "></div>`,
        iconSize: [markerSize, markerSize],
        iconAnchor: [markerSize/2, markerSize/2]
      }));

      // Update dropoff marker style
      dropoff.setIcon(L.divIcon({
        className: 'custom-dropoff-marker',
        html: `<div style="
          background-color: ${dropoffColor};
          width: ${markerSize}px;
          height: ${markerSize}px;
          border-radius: 50%;
          border: ${borderWidth}px solid white;
          box-shadow: 0 2px 4px rgba(0,0,0,0.3);
          cursor: pointer;
        "></div>`,
        iconSize: [markerSize, markerSize],
        iconAnchor: [markerSize/2, markerSize/2]
      }));

      // Update route line style
      const lineColor = isSelected ? '#2196f3' : (hasSelection && !isSelected) ? '#9e9e9e' : '#2196f3';
      const lineWeight = isSelected ? 4 : (hasSelection && !isSelected) ? 2 : 2;
      const lineOpacity = isSelected ? 1 : (hasSelection && !isSelected) ? 0.6 : 0.6;
      
      line.setStyle({
        color: lineColor,
        weight: lineWeight,
        opacity: lineOpacity
      });
    });
  }, [selectedRideIndex]);

  const totalRides = rides.length;
  const uniquePickupLocations = new Set(rides.map(r => `${r.from_latitude},${r.from_longitude}`)).size;
  const uniqueDropoffLocations = new Set(rides.map(r => `${r.to_latitude},${r.to_longitude}`)).size;
  
  const selectedRide = selectedRideIndex !== null ? rides[selectedRideIndex] : null;

  return (
    <Paper sx={{ p: 2, mt: 2 }}>
      <Typography variant="h6" gutterBottom>
        {t('setup.rideMap.title')}
      </Typography>
      
      
      <Box sx={{ position: 'relative' }}>
        <Box
          ref={mapRef}
          sx={{
            height: 400,
            borderRadius: 1,
            mb: 2,
            border: '1px solid #e0e0e0'
          }}
        />
        
        {/* Ride Information Overlay */}
        {selectedRide && (
          <Paper
            sx={{
              position: 'absolute',
              top: 10,
              right: 10,
              width: 300,
              maxWidth: 'calc(100% - 20px)',
              bgcolor: 'rgba(255, 255, 255, 0.95)',
              backdropFilter: 'blur(4px)',
              border: '1px solid #e0e0e0',
              borderRadius: 2,
              boxShadow: 3,
              zIndex: 1000
            }}
          >
            <Box sx={{ p: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="subtitle2" fontWeight="bold">
                  {t('setup.rideMap.rideNumber', { number: selectedRideIndex! + 1 })}
                </Typography>
                <Button
                  size="small"
                  onClick={() => {
                    setSelectedRideIndex(null);
                  }}
                  sx={{ minWidth: 'auto', p: 0.5 }}
                >
                  ✕
                </Button>
              </Box>
              
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1.5 }}>
                <Chip
                  label={t('setup.rideMap.passengersChip', { count: selectedRide.passengers[0]?.number_of_passengers || 1 })}
                  size="small"
                  color="primary"
                />
                <Chip 
                  label={selectedRide.passengers[0]?.type || 'standard'}
                  size="small"
                  color={selectedRide.passengers[0]?.type === 'wheelchair' ? 'secondary' : 'default'}
                />
                <Chip 
                  label={selectedRide.request_type || 'departure_fixed'}
                  size="small"
                  variant="outlined"
                />
              </Box>
              
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Box>
                  <Typography variant="caption" color="success.main" fontWeight="bold">
                    📍 {t('setup.rideMap.from')}
                  </Typography>
                  <Typography variant="body2" fontSize="0.8rem">
                    {selectedRide.from_address || `${selectedRide.from_latitude.toFixed(4)}, ${selectedRide.from_longitude.toFixed(4)}`}
                  </Typography>
                </Box>
                
                <Box>
                  <Typography variant="caption" color="error.main" fontWeight="bold">
                    🎯 {t('setup.rideMap.to')}
                  </Typography>
                  <Typography variant="body2" fontSize="0.8rem">
                    {selectedRide.to_address || `${selectedRide.to_latitude.toFixed(4)}, ${selectedRide.to_longitude.toFixed(4)}`}
                  </Typography>
                </Box>
                
                {selectedRide.request_time && (
                  <Box>
                    <Typography variant="caption" color="primary.main" fontWeight="bold">
                      🕒 {t('setup.rideMap.time')}
                    </Typography>
                    <Typography variant="body2" fontSize="0.8rem">
                      {new Date(selectedRide.request_time).toLocaleString()}
                    </Typography>
                  </Box>
                )}
              </Box>
            </Box>
          </Paper>
        )}
      </Box>
      
      <Box sx={{ display: 'flex', gap: 4 }}>
        <Box>
          <Typography variant="body2" fontWeight="bold">
            {totalRides}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {t('setup.rideMap.totalRides')}
          </Typography>
        </Box>
        <Box>
          <Typography variant="body2" fontWeight="bold">
            {uniquePickupLocations}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {t('setup.rideMap.pickupLocations')}
          </Typography>
        </Box>
        <Box>
          <Typography variant="body2" fontWeight="bold">
            {uniqueDropoffLocations}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {t('setup.rideMap.dropoffLocations')}
          </Typography>
        </Box>
      </Box>

      <Box sx={{ mt: 2, display: 'flex', gap: 3, alignItems: 'center', flexWrap: 'wrap' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box sx={{ width: 12, height: 12, backgroundColor: '#4caf50', borderRadius: '50%', border: '2px solid white' }} />
          <Typography variant="caption" color="text.secondary">{t('setup.rideMap.pickup')}</Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box sx={{ width: 12, height: 12, backgroundColor: '#f44336', borderRadius: '50%', border: '2px solid white' }} />
          <Typography variant="caption" color="text.secondary">{t('setup.rideMap.dropoff')}</Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box sx={{ width: 20, height: 2, backgroundColor: '#2196f3' }} />
          <Typography variant="caption" color="text.secondary">{t('setup.rideMap.route')}</Typography>
        </Box>
        <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
          {t('setup.rideMap.instructions')}
        </Typography>
      </Box>

    </Paper>
  );
};
